import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

import type { ImpersonateResponse } from '@common/api/auth';
import { enqueueSnackbar } from '@common/components/SnackBar';
import { getApiErrorMessages } from '@common/helpers/handleApiErrors';
import { RefreshWaitingQueue } from '@common/helpers/refreshWaitingQueue';
import { getAuth, removeAuth, saveAuth } from '@common/models/Auth/storage';

type RefreshTokenResponse = ImpersonateResponse & {
	centrifugo_channel?: string;
};

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again to continue.';

const createResponseErrorInterceptor = (apiInstance: AxiosInstance) => {
	const refreshQueue = new RefreshWaitingQueue((request) => apiInstance(request));
	let isLoggingOut = false;

	const handleLogout = () => {
		if (isLoggingOut) return;
		isLoggingOut = true;

		removeAuth();
		delete apiInstance.defaults.headers.common.Authorization;
		if (typeof window !== 'undefined') {
			window.location.reload();
		}
	};

	const refreshAccessToken = async () => {
		const refreshClient = axios.create({
			baseURL: apiInstance?.defaults?.baseURL || axios?.defaults?.baseURL,
		});

		const auth = getAuth();
		const { data: refreshData } = await refreshClient.get<RefreshTokenResponse>('/refresh-token', {
			headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : undefined,
		});
		return refreshData;
	};

	return async (error: AxiosError) => {
		const originalRequest = (error.config || {}) as AxiosRequestConfig & {
			_retry?: boolean;
			skipErrorNotification?: boolean;
		};

		const status = error.response?.status;
		const requestUrl = originalRequest?.url || '';
		const currentAuth = getAuth();
		const hasToken = !!currentAuth.token;
		const isAuthRequest = requestUrl.includes('/login') || requestUrl.includes('/staff/login');

		if (!status) {
			return Promise.reject(error);
		}

		if (error.code === 'ERR_CANCELED') {
			return Promise.reject(error);
		}

		if ((status === 401 || status === 406) && requestUrl.includes('/refresh-token')) {
			refreshQueue.rejectQueued(error);
			refreshQueue.isRefreshing = false;
			handleLogout();
			enqueueSnackbar(SESSION_EXPIRED_MESSAGE, { variant: 'error' });
			return Promise.reject(error);
		}

		if (status === 401 && hasToken && !isAuthRequest && !originalRequest._retry) {
			originalRequest._retry = true;

			if (refreshQueue.isRefreshing) {
				return refreshQueue.push(originalRequest);
			}

			refreshQueue.isRefreshing = true;
			try {
				const { token, centrifugo_channel: centrifugoChannel } = await refreshAccessToken();
				const authPayload = {
					...currentAuth,
					token,
					centrifugo_channel: centrifugoChannel ?? currentAuth.centrifugo_channel,
				};

				saveAuth(authPayload);
				apiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;

				refreshQueue.isRefreshing = false;
				refreshQueue.resolveQueued(token);

				const headers = originalRequest.headers || {};
				headers.Authorization = `Bearer ${token}`;
				originalRequest.headers = headers;

				return await apiInstance(originalRequest);
			} catch (refreshError: any) {
				refreshQueue.rejectQueued(refreshError);
				refreshQueue.isRefreshing = false;
				handleLogout();
				enqueueSnackbar(SESSION_EXPIRED_MESSAGE, { variant: 'error' });

				return Promise.reject(refreshError);
			}
		}

		if ((status === 401 || status === 406) && hasToken) {
			handleLogout();
			enqueueSnackbar(SESSION_EXPIRED_MESSAGE, { variant: 'error' });
		} else if (
			!originalRequest.skipErrorNotification &&
			!getApiErrorMessages(error.response?.data).length
		) {
			enqueueSnackbar(error.message, {
				variant: 'error',
			});
		}

		return Promise.reject(error);
	};
};

export default createResponseErrorInterceptor;
